<template>
  <div
    class="overlayResults"
    v-if="products && products.length"
  >
    <span
      v-if="isAnySearch"
      class="anysearch-warning"
    >
      {{ $tv({messageKey: 'warningAnySearch', messageValues: {search: searchTerm} }, 'searchResults') }}
    </span>
    <div class="overlayResults__tableheader overlayResults__tile">
      <span class="img-block overlayResults__tile__image" />
      <span class="text-block overlayResults__tile__description" />
      <span class="overlayResults__tile__icon" />
    </div>
    <div
      v-for="(product, index) in products"
      :key="index"
      @click="closeOverlay"
    >
      <div
        @click="clickItem(product?.sku, index + 1)"
        class="overlayResults__tile"
      >
        <div class="overlayResults-left">
          <nuxt-link
            :to="getLocalePath(product)"
            class="overlayResults__tile__image"
          >
            <img
              :src="imageUrl(product, true)"
              :alt="product.sku"
            >
          </nuxt-link>
          <nuxt-link
            :to="getLocalePath(product)"
            class="pure-u-3-4 overlayResults__tile__description"
          >
            <div class="overlayResults__tile__description_wrapper">
              <div
                class="ellipsis"
                v-html="getProductText(product)"
              />
              <div class="highlights">
                <span v-tv:search="'foundIn'" />
                <span
                  class="highlight"
                  v-text="foundInText(product)"
                />
              </div>
            </div>
          </nuxt-link>
        </div>
        <div class="overlayResults-right">
          <div class="overlayResults-right__sku-stock">
            <nuxt-link
              :to="getLocalePath(product)"
            >
              <div class="overlayResults__tile__sku">
                <SKUContainer
                  :product="product"
                  :multiple-lines="true"
                  :show-values-in-row="true"
                />
              </div>
            </nuxt-link>
            <div
              v-if="shouldShowAvailability(product) && !isAvailabilityLoaded(product)"
              class="overlayResults__tile__stock overlayResults__tile__stock--skeleton"
            >
              <Skeleton
                shape="circle"
                class="stock-skeleton__circle"
              />
              <Skeleton
                shape="rectangle"
                class="stock-skeleton__line"
              />
            </div>
            <div
              v-else-if="shouldShowAvailability(product)"
              class="overlayResults__tile__stock"
            >
              <ImageCircle
                v-if="isAvailable(product)"
                class="svg-image-circle icon--svg circle--available"
              />
              <OutlineCircle
                v-else
                class="svg-outline-circle icon--svg circle--not-available"
              />
              <span
                class="stock-quantity"
                v-text="getCurrentStock(product)"
              />
            </div>
          </div>
          <div>
            <a
              href="javascript:void(0)"
              class="overlayResults__tile__icon"
              :class="{disabled: !isBuyable(product)}"
              @click.prevent="openBuyBox(product)"
            >
              <ActionShoppingCart
                class="svg-action-shopping-cart"
                :title="$tv('users.searchShoppingCartIconTooltip', 'management')"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="overlayResults__information">
      <div
        class="overlayResults__information--center"
        v-tv:searchResults="'ThereAreMoreProducts'"
      />
    </div>
    <div
      v-if="l2categories.length > 0"
      class="overlayResults__categories"
    >
      <div
        class="overlayResults__categories__headline"
        v-tv:searchResults="'MatchingProductCategories'"
      />
      <div class="overlayResults__categories__results">
        <div
          v-for="(primaryL2Category, index) in l2categories"
          :key="index"
        >
          <nuxt-link
            :to="`${resultPath}&${primaryL2CategoryName}=${primaryL2Category.categoryName}`"
          >
            <div
              class="overlayResults__categories__results__tile"
              @click="closeOverlay"
            >
              <div
                class="overlayResults__categories__results__tile__image"
              >
                <img
                  :src="imageUrl(primaryL2Category.categoryName)"
                  :alt="primaryL2Category.categoryName"
                >
              </div>
              <div class="overlayResults__categories__results__tile__description">
                <span
                  class="ellipsis"
                  v-text="`${$tv(primaryL2Category?.categoryName, 'categories')} (${primaryL2Category?.hitCount?.toLocaleString($i18n.locale === 'en' ? 'en' : 'de')})`"
                />
              </div>
            </div>
          </nuxt-link>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {CATEGORIES, NON_ATTRIBUTE_FILTERS, USER_RIGHTS_MAPPING, REQUEST_ONLY_STATUS} from '~/assets/js/constants'
import {useItemImageUrlHelper} from '~/composables/item_image_url_helper'
import ActionShoppingCart from '~/assets/icons/action_shopping_cart_plus.svg?component'
import ImageCircle from '~/assets/icons/image_circle.svg?component'
import OutlineCircle from '~/assets/icons/outline_circle.svg?component'
import {useDefaultCustomer} from '~/composables/default_customer'
import SKUContainer from '~/components/SKUContainer'
import {useRightsHelper} from '~/composables/rights_helper'
import {Skeleton} from '@progress/kendo-vue-indicators'

export default {
  components: {ActionShoppingCart, SKUContainer, ImageCircle, OutlineCircle, Skeleton},
  props: {
    products: {
      type: Array,
      default () {
        return []
      }
    },
    l2categories: {
      type: Array,
      default () {
        return []
      }
    },
    categories: {
      type: Array,
      default () {
        return []
      }
    },
    searchTerm: {
      type: String,
      default() {
        return ''
      }
    },
    searchFilter: {
      type: Array,
      default () {
        return []
      }
    },
    isInchSearch: {
      type: Boolean,
      default() {
        return false
      }
    },
    isAnySearch: {
      type: Boolean,
      default() {
        return false
      }
    },
    availabilities: {
      type: Array,
      default() {
        return []
      }
    },
    loadedAvailabilitySkus: {
      type: Array,
      default() {
        return []
      }
    }
  },
  setup() {
    const {isDefaultCustomer} = useDefaultCustomer()
    const {imageUrl: imageUrlHelper} = useItemImageUrlHelper()
    const {userHasRight} = useRightsHelper()
    return {
      isDefaultCustomer,
      imageUrlHelper,
      userHasRight
    }
  },
  data() {
    return {
      rootCategoriesUrl: CATEGORIES.RootCategoriesUrl,
      primaryL2CategoryName: NON_ATTRIBUTE_FILTERS.PRIMARYL2CATEGORY,
      USER_RIGHTS_MAPPING,
      REQUEST_ONLY_STATUS
    }
  },
  computed: {
    resultPath() {
      const path = this.localePath({name: 'searchResults'})
      return path + '?query=' + encodeURIComponent(this.searchTerm)
    }
  },
  methods: {
    isAvailabilityLoaded(product) {
      return this.loadedAvailabilitySkus.includes(product?.sku)
    },
    getAvailability(product) {
      const productStatus = product?.salesAreas && product?.salesAreas.length > 0 ? product.salesAreas[0].status : null
      return this.userHasRight(this.USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) && !this.REQUEST_ONLY_STATUS.includes(productStatus)
        ? this.availabilities?.find(a => a?.sku === product?.sku) ?? null
        : null
    },
    getCurrentStock(product) {
      const availability = this.getAvailability(product)
      if (!this.userHasRight(this.USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) || !availability?.availabilities) {
        return '-'
      }

      const totalStock = availability.availabilities
        .filter(a => !a.plantIsDisplayOnly)
        .reduce((sum, a) => sum + (a.currentStock?.quantity || 0), 0)

      const unit = availability.availabilities?.find(a => a.currentStock?.unit)?.currentStock?.unit

      return totalStock > 0 ? this.getQuantityDisplay(totalStock, unit) : '-'
    },
    isAvailable(product) {
      const availability = this.getAvailability(product)
      if (!availability?.availabilities) {
        return false
      }
      const totalStock = availability.availabilities
        .filter(a => !a.plantIsDisplayOnly)
        .reduce((sum, a) => sum + (a.currentStock?.quantity || 0), 0)
      return totalStock > 0
    },
    getQuantityDisplay(quantity, unit) {
      const formattedQuantity = quantity.toLocaleString(this.$i18n.locale === 'en' ? 'en' : 'de')
      return this.$tv({messageKey: 'quantityAndUnit', messageValues: {quantity: formattedQuantity, unit: this.$tv(unit, 'packageUnits')}})
    },
    shouldShowAvailability(product) {
      const productStatus = product?.salesAreas && product?.salesAreas.length > 0 ? product.salesAreas[0].status : null
      return this.userHasRight(this.USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) && !this.REQUEST_ONLY_STATUS.includes(productStatus)
    },
    imageUrl(item, isProduct = false) {
      return this.imageUrlHelper(item, isProduct, this.categories)
    },
    closeOverlay(event) {
      this.$emit('close-overlay', event)
    },
    isBuyable(product) {
      return (((product?.requestable ?? false) || (product?.buyable ?? false) || this.isDefaultCustomer) && this.$auth.loggedIn) || !this.$auth.loggedIn
    },
    openBuyBox(product) {
      if (this.isBuyable(product)) {
        this.$emit('openBuyBox', product)
      }
    },
    getLocalePath(product) {
      return this.localePath('/products' + '/' + product.sku, this.$i18n.locale)
    },
    getProductText(product) {
      if (product.highlights && product.highlights.display?.length > 0) {
        return product.highlights.display[0]
      }
      const eComText = this.isInchSearch ? product['locales_' + this.$i18n.locale.toUpperCase() + '_Imperial_SalesTextECom'] : product['locales_' + this.$i18n.locale.toUpperCase() + '_Metric_SalesTextECom']
      return eComText
    },
    clickItem: function(sku, rank) {
      this.$emit('clickItem', sku, rank)
    },
    foundInText(product) {
      let highlights = Object.keys(product.highlights).filter(k => k !== 'display')
      highlights = highlights.map(h => this.$tv(h, 'search'))
      return highlights.join(', ')
    }
  }
}
</script>

<style lang="scss">
.overlayResults {
  a {
    text-decoration: none;
  }

  &__tableheader.overlayResults__tile {
    display: none;
    box-shadow: none;
    background-color: transparent;
    color: $color__text__default;
    min-height: unset;

    .img-block {
      width: 3rem;
    }

    .text-block {
      width: 75%;
    }

    @include breakpoint-up($md) {
      display: flex;
    }

  }

  .overlayResults__tile  {
    .overlayResults-left  {
      width: 100%;

      > a {
        display: flex;
        align-items: center;
        color: $color__text__default;
      }

      .overlayResults__tile__image {
        align-items: flex-end;

        img {
          width: 3rem;
        }
      }
    }

    .overlayResults-right {
      display: flex;
      align-items: center;

      &__sku-stock {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
    }

    .overlayResults--right {
      width: 50%;
      justify-content: flex-end;
    }
  }

  &__tile {
    background-color: $color__white;
    margin-bottom: .125rem;
    display: flex;
    justify-content: space-between;
    color: $color__text__default;
    box-shadow: none;
    min-height: 3.125rem;

    @include breakpoint-down($md) {
      box-shadow: 0 .1875rem .375rem $color--card__shadow;
    }

    > div,
    > a {
      display: flex;
      align-items: center;
      color: $color__text__default;
    }

    &__description {
      width: 100%;
      overflow: auto;
      font-size: $base-text-font-size;

      &_wrapper {
        overflow: hidden;
        display: flex;
        flex-direction: column;
        gap: .25rem;

        .highlights {
          display: flex;
          gap: .25rem;
          font-size: .65rem;
          align-content: center;

          span {
            padding: .1875rem 0;
          }
        }
      }

      > div {
        font-weight: $base-text-font-weight-light;
        padding: 0 $default-margin-padding-width;
      }

      .ellipsis {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: $base-text-font-size;

        em {
          font-style: normal;
          font-weight: $base-text-font-weight-bold;
        }

        @include breakpoint-down($md) {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          white-space: normal;
        }
      }
    }

    &__sku {
      color: $color__primary;
      width: 100%;
      font-weight: $base-text-font-weight-bold;
      padding: 0 0 .3rem;
      display: flex;
      flex-direction: row;
      font-size: $base-text-font-size;

      @include breakpoint-down($md) {
        display: none;
      }
    }

    &__stock {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: flex-end;
      gap: .25rem;

      .svg-image-circle,
      .svg-outline-circle {
        font-size: .8rem;
      }

      .circle--available {
        color: $color--input__border--ok;
      }

      .circle--not-available {
        color: $color--input__border--error;
      }

      .stock-quantity {
        font-weight: $base-text-font-weight-light;
        font-size: .75rem;
        white-space: nowrap;
      }

      &--skeleton {
        .stock-skeleton__circle {
          width: .75rem;
          height: .75rem;
        }

        .stock-skeleton__line {
          width: 2.5rem;
          height: .6rem;
        }
      }

      @include breakpoint-down($md) {
        display: none;
      }
    }

    .overlayResults__tile__image {
      align-items: flex-end;

      img {
        width: 3rem;
      }
    }

    &__icon {
      padding: 0 .625rem 0 1rem;

      .icon--svg.svg-action-shopping-cart {
        font-size: 1.8rem;
      }

      .icon--svg {
        color: $color__primary;
      }

      &.disabled {
        pointer-events: none;

        .icon--svg {
          color: $color__page--background--opacity;
        }
      }
    }
  }

  &__information {
    display: flex;
    font-weight: $base-text-font-weight-light;
    justify-content: space-between;
    margin-top: .5rem;
    margin-bottom: 0;

    @include breakpoint-down($md) {
      display: inline;

      div {
        margin-bottom: $default-margin-padding-width;
      }
    }

    &--center {
      margin-top: 0;
    }
  }

  &__categories {
    display: flex;
    flex-direction: column;
    margin-top: 1.5rem;

    &__headline {
      font-size: 1rem;
      font-weight: $base-text-font-weight-bold;
      color: $color__primary;
      margin-bottom: .5rem;
    }

    &__results {
      display: flex;
      flex-wrap: wrap;
      gap: .75rem;

      &__tile {
        background-color: $color__white;
        margin: 0 0 .125rem;
        display: flex;
        color: $color__primary;
        font-weight: $base-text-font-weight-bold;
        box-shadow: 0 .1875rem .375rem $color--card__shadow;
        min-height: 3.125rem;
        min-width: 14rem;

        > div {
          display: flex;
          align-items: center;
        }

        &__image {
          align-items: flex-end;

          img {
            width: 3rem;
          }
        }

        &__description {
          padding: 0 .5rem;
        }
      }
    }
  }

  .pure-u-1-2:nth-child(odd) {
    .overlayResults__designs__tile {
      margin-right: $default-margin-padding-width;
    }
  }

  .h5 {
    margin-bottom: $default-margin-padding-width;
  }
}
</style>
